import React, { useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  InputNumber,
  Image,
  Alert,
  Empty,
  Spin,
  Row,
  Col,
  Divider,
  Popconfirm,
  Tag,
  message,
  Tooltip,
  Grid
} from 'antd';
import {
  DeleteOutlined,
  ShoppingCartOutlined,
  ClearOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useCartStore } from '../stores/cartStore';
import { CartItem } from '../services/cartService';
import { ProductService } from '../services/productService';

const { Title, Text, Paragraph } = Typography;

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const {
    items,
    summary,
    loading,
    error,
    validation,
    fetchCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    validateCart,
    clearError
  } = useCartStore();

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleQuantityChange = async (supersedeNoUOM: string, quantity: number) => {
    try {
      await updateCartItem(supersedeNoUOM, quantity);
    } catch (error) {
      message.error('Failed to update quantity');
    }
  };

  const handleRemoveItem = async (item: CartItem) => {
    try {
      await removeFromCart(item.SupersedeNoUOM);
      message.success(`Removed ${item.Description} from cart`);
    } catch (error) {
      message.error('Failed to remove item');
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
      message.success('Cart cleared successfully');
    } catch (error) {
      message.error('Failed to clear cart');
    }
  };

  const handleValidateCart = async () => {
    try {
      await validateCart();
      if (!validation?.hasChanges) {
        message.success('Cart is up to date');
      }
    } catch (error) {
      message.error('Failed to validate cart');
    }
  };

  const handleProceedToCheckout = () => {
    if (items.length === 0) {
      message.warning('Your cart is empty. Please add items before checkout.');
      return;
    }

    navigate('/checkout');
  };

  const columns = [
    {
      title: 'Product',
      key: 'product',
      render: (record: CartItem) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Image
            width={60}
            height={60}
            src={ProductService.getProductImageUrl(record.MainPictureFull)}
            fallback="/images/product-placeholder.svg"
            preview={false}
            style={{ borderRadius: '8px', objectFit: 'cover' }}
          />
          <div>
            <Text strong>{record.Description}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '0.9em' }}>
              {record.UOM} • {record.SupersedeNo}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Price',
      key: 'price',
      render: (record: CartItem) => (
        <div>
          <Text strong style={{ color: 'var(--trust-blue)' }}>
            {record.PriceDesc1}
          </Text>
          {record.PriceDesc2 && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: '0.9em' }}>
                {record.PriceDesc2}
              </Text>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (record: CartItem) => (
        <InputNumber
          min={1}
          value={record.Qty}
          onChange={(value) => {
            if (value && value > 0) {
              handleQuantityChange(record.SupersedeNoUOM, value);
            }
          }}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: 'Total',
      key: 'total',
      render: (record: CartItem) => (
        <Text strong style={{ fontSize: '1.1em', color: 'var(--trust-blue)' }}>
          ${record.TotalPrice.toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: CartItem) => (
        <Popconfirm
          title="Remove item"
          description="Are you sure you want to remove this item from your cart?"
          onConfirm={() => handleRemoveItem(record)}
          okText="Remove"
          cancelText="Cancel"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            loading={loading}
          >
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // Mobile: stacked cards instead of a wide table that overflows the viewport.
  const renderMobileItems = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {items.map((record) => (
        <Card key={record.SupersedeNoUOM} size="small" style={{ borderRadius: 8 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Image
              width={64}
              height={64}
              src={ProductService.getProductImageUrl(record.MainPictureFull)}
              fallback="/images/product-placeholder.svg"
              preview={false}
              style={{ borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong>{record.Description}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '0.85em' }}>
                {record.UOM} • {record.SupersedeNo}
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text style={{ color: 'var(--trust-blue)' }}>{record.PriceDesc1}</Text>
              </div>
            </div>
          </div>
          <Divider style={{ margin: '10px 0' }} />
          <Row align="middle" justify="space-between" gutter={[8, 8]}>
            <Col>
              <Space size="small">
                <Text type="secondary">Qty</Text>
                <InputNumber
                  min={1}
                  value={record.Qty}
                  onChange={(value) => {
                    if (value && value > 0) handleQuantityChange(record.SupersedeNoUOM, value);
                  }}
                  style={{ width: 72 }}
                />
              </Space>
            </Col>
            <Col>
              <Text strong style={{ fontSize: '1.1em', color: 'var(--trust-blue)' }}>
                ${record.TotalPrice.toFixed(2)}
              </Text>
            </Col>
            <Col>
              <Popconfirm
                title="Remove item"
                description="Remove this item from your cart?"
                onConfirm={() => handleRemoveItem(record)}
                okText="Remove"
                cancelText="Cancel"
              >
                <Button type="text" danger icon={<DeleteOutlined />} loading={loading} />
              </Popconfirm>
            </Col>
          </Row>
        </Card>
      ))}
    </Space>
  );

  if (loading && items.length === 0) {
    return (
      <div className="cart-page" style={{ padding: 'var(--spacing-lg)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page" style={{ padding: 'var(--spacing-lg)' }}>
      <div className="container">
        {/* Page Header */}
        <div style={{ marginBottom: 'var(--spacing-2xl)' }}>
          <Title level={2} style={{ color: 'var(--dasoon-black)', marginBottom: 'var(--spacing-md)', fontFamily: 'var(--font-headers)', letterSpacing: 'var(--font-headers-spacing)' }}>
            <ShoppingCartOutlined style={{ marginRight: 'var(--spacing-sm)' }} />
            Shopping Cart
          </Title>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            closable
            onClose={clearError}
            showIcon
            style={{ marginBottom: 'var(--spacing-lg)' }}
          />
        )}

        {/* Validation Alert */}
        {validation?.hasChanges && (
          <Alert
            message="Cart Updated"
            description={
              <div>
                <Paragraph style={{ marginBottom: 'var(--spacing-sm)' }}>
                  Some items in your cart have been updated:
                </Paragraph>
                {validation.priceChanges.length > 0 && (
                  <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                    <Text strong>Price changes:</Text>
                    <ul>
                      {validation.priceChanges.map(item => (
                        <li key={item.SupersedeNoUOM}>{item.Description}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {validation.invalidItems.length > 0 && (
                  <div>
                    <Text strong>Unavailable items removed:</Text>
                    <ul>
                      {validation.invalidItems.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 'var(--spacing-lg)' }}
          />
        )}

        {/* Cart Content */}
        {items.length === 0 ? (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text type="secondary" style={{ fontSize: '1.1em' }}>
                    Your cart is empty
                  </Text>
                  <br />
                  <Text type="secondary">
                    Add some products to get started
                  </Text>
                </div>
              }
            >
              <Link to="/products">
                <Button type="primary" size="large">
                  Browse Products
                </Button>
              </Link>
            </Empty>
          </Card>
        ) : (
          <Row gutter={[24, 24]}>
            {/* Cart Items */}
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space>
                    <Text style={{ fontSize: '1.1em' }}>
                      Cart Items ({items.length})
                    </Text>
                    <Tooltip title="Validate and update cart prices">
                      <Button
                        type="text"
                        icon={<SyncOutlined />}
                        onClick={handleValidateCart}
                        loading={loading}
                        size="small"
                      />
                    </Tooltip>
                  </Space>
                }
                extra={
                  <Popconfirm
                    title="Clear cart"
                    description="Are you sure you want to remove all items from your cart?"
                    onConfirm={handleClearCart}
                    okText="Clear"
                    cancelText="Cancel"
                  >
                    <Button 
                      danger 
                      icon={<ClearOutlined />}
                      loading={loading}
                    >
                      Clear Cart
                    </Button>
                  </Popconfirm>
                }
              >
                {screens.md ? (
                  <Table
                    dataSource={items}
                    columns={columns}
                    pagination={false}
                    rowKey="SupersedeNoUOM"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                  />
                ) : (
                  renderMobileItems()
                )}
              </Card>
            </Col>

            {/* Cart Summary */}
            <Col xs={24} lg={8}>
              <Card title="Order Summary" style={{ position: 'sticky', top: '20px' }}>
                {summary && (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Items ({summary.itemCount}):</Text>
                      <Text>{summary.totalQuantity} units</Text>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>{summary.calculation.totalStr || 'Subtotal:'}</Text>
                      <Text strong>
                        {summary.calculation.currencyCodeStr}{summary.calculation.totalD.toFixed(2)}
                      </Text>
                    </div>

                    {summary.calculation.deliveryAmtD > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>{summary.calculation.deliveryAmtStr}</Text>
                        <Text>
                          {summary.calculation.currencyCodeStr}{summary.calculation.deliveryAmtD.toFixed(2)}
                        </Text>
                      </div>
                    )}

                    {summary.calculation.gstAmtD > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>{summary.calculation.gstAmtStr}</Text>
                        <Text>
                          {summary.calculation.currencyCodeStr}{summary.calculation.gstAmtD.toFixed(2)}
                        </Text>
                      </div>
                    )}

                    <Divider style={{ margin: '12px 0' }} />

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1em' }}>
                      <Text strong>
                        {summary.calculation.gTotalStr || `Total ${summary.calculation.currencyCodeStr}:`}
                      </Text>
                      <Text strong style={{ color: 'var(--dasoon-primary)', fontSize: '1.2em' }}>
                        {summary.calculation.currencyCodeStr}
                        {(summary.calculation.gTotalD || summary.calculation.totalD).toFixed(2)}
                      </Text>
                    </div>

                    {summary.calculation.gstStatusStr && (
                      <div style={{ marginTop: 'var(--spacing-sm)' }}>
                        <Tag color="gold">{summary.calculation.gstStatusStr}</Tag>
                      </div>
                    )}

                    <Button
                      type="primary" className="dasoon-btn-primary"
                      size="large"
                      style={{ width: '100%', marginTop: 'var(--spacing-lg)' }}
                      disabled={items.length === 0}
                      onClick={handleProceedToCheckout}
                    >
                      Proceed to Checkout
                    </Button>

                    <Button
                      type="default"
                      size="large"
                      style={{ width: '100%', marginTop: 'var(--spacing-sm)' }}
                    >
                      <Link to="/products" style={{ color: 'inherit', textDecoration: 'none' }}>Continue Shopping</Link>
                    </Button>
                  </Space>
                )}
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default Cart;
